import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { SlideOutHeight } from "../atoms/animations/SlideOutHeight"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { theme } from "../theme"
import { MediaQuery } from "../../utils/design-helpers"
import { BillingAddress, OrganizationType } from "../../utils/models"
import Card from "../atoms/Card"
import CardBar from "../atoms/CardBar"
import H3 from "../atoms/typography/H3"
import Subtitle from "../atoms/typography/Subtitle"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import SecondaryButton from "../atoms/buttons/SecondaryButton"
import SmallButton from "../atoms/buttons/SmallButton"
import { AccountTreeIcon } from "../atoms/icons/AccountTreeIcon"
import { AddTerminalIcon } from "../atoms/icons/AddTerminalIcon"
import { EditIcon } from "../atoms/icons/EditIcon"
import { GroupsIcons } from "../atoms/icons/GroupsIcon"
import { PersonIcon } from "../atoms/icons/PersonIcon"
import { RssFeedIcon } from "../atoms/icons/RssFeedIcon"
import AddressForm, { DEFAULT_BILLING_ADDRESS_INFO, updateAddress } from "../molecules/AddressForm"
import MetricChip from "../molecules/MetricChip"
import PendingInvitesCard, { PendingInviteApiType } from "../molecules/PendingInviteCard"
import { useUpdateOrgAddress } from "../../hooks/useUpdateOrgAddress"
import InviteMemberToOrganizationButton from "../molecules/InviteMemberToOrganizationButton"
import Body from "../atoms/typography/Body"
import { ArrowDropDownIcon } from "../atoms/icons/ArrowDropDownIcon"
import { ArrowDropUpIcon } from "../atoms/icons/ArrowDropUpIcon"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../utils/sentry"

interface Props {
  organization: OrganizationType
  pendingInvites: PendingInviteApiType[]
  open: boolean
  onClickClose: () => void
  isEditing: boolean
}

const MetricChipsColumn = styled(Col)`
  display: flex;
  justify-content: space-around;
  align-items: center;

  ${MediaQuery.XS} {
    flex-direction: column;
  }
`

const ContentContainer = styled.div`
  padding: ${(props) => props.theme.spacings.extreme};
`

const SuborganizationOverview: React.FunctionComponent<Props> = ({
  organization,
  open,
  onClickClose,
  isEditing,
  pendingInvites,
}) => {
  const { t } = useTranslation()

  const [billingAddress, setBillingAddress] = React.useState<BillingAddress>(DEFAULT_BILLING_ADDRESS_INFO)
  const [editingBilling, setEditingBilling] = React.useState(false)
  const [hasUpdatedBillingAddress, setHasUpdatedBillingAddress] = React.useState(false)

  React.useMemo(() => {
    setBillingAddress(updateAddress(DEFAULT_BILLING_ADDRESS_INFO, organization.billingAddress))
  }, [organization])

  const handleChangeBillingAddress = (event: any) => {
    const name = event.target.name
    const value = event.target.value

    setBillingAddress((values: any) => ({ ...values, [name]: value }))
    setHasUpdatedBillingAddress(true)
  }

  const onCompletedUpdateBillingAddress = () => {
    setEditingBilling(false)
    setHasUpdatedBillingAddress(false)
  }

  const onErrorUpdateBillingAddress = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, `error updating billing address for organization ${organization.id}`)
  }

  const { updateOrgAddress, data } = useUpdateOrgAddress({
    onCompleted: onCompletedUpdateBillingAddress,
    onError: onErrorUpdateBillingAddress,
  })

  const onUpdateAddress = async () => {
    try {
      await updateOrgAddress({
        variables: {
          input: {
            id: organization.id,
            ...updateAddress(DEFAULT_BILLING_ADDRESS_INFO, billingAddress),
          },
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, `error updating billing address for organization ${organization.id}`)
    }
  }

  const onSaveUpdateAddress = (event: React.FormEvent) => {
    event.preventDefault()
    onUpdateAddress()
  }

  return (
    <div>
      <Card noPadding>
        <CardBar open={open}>
          <Row justify="between" align="center">
            <Col lg={3} md={8} xs={8}>
              <Subtitle color={theme.colors.white}>{organization.name}</Subtitle>
            </Col>
            <Hidden xs md>
              <Col lg={6}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Body color={theme.colors.white}>
                    {organization.stats?.members} {t("members")}
                  </Body>
                  <Spacer spacing={2} />
                  <Body color={theme.colors.white}>
                    {organization.stats?.groups} {t("groups")}
                  </Body>
                  <Spacer spacing={2} />

                  <Body color={theme.colors.white}>
                    {organization.stats?.terminals} {t("terminals")}
                  </Body>
                </div>
              </Col>
            </Hidden>
            <Col md={3} xs={4}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <SmallButton
                  title={open ? t("common.hide") : t("common.open")}
                  icon={
                    open ? (
                      <ArrowDropUpIcon color={theme.colors.greyDark} />
                    ) : (
                      <ArrowDropDownIcon color={theme.colors.greyDark} />
                    )
                  }
                  onClick={onClickClose}
                />
              </div>
            </Col>
          </Row>
        </CardBar>
        <SlideOutHeight isVisible={open}>
          <ContentContainer>
            <HorizontalSpacer spacing={4} />
            {isEditing && (
              <div>
                <Row justify="center">
                  <InviteMemberToOrganizationButton orgName={organization.name} orgId={organization.id} />
                </Row>
                <HorizontalSpacer spacing={2} />
              </div>
            )}
            <Row justify="center">
              <MetricChipsColumn lg={8}>
                <MetricChip icon={<PersonIcon />} value={`${organization.stats?.members} Members`} />
                <MetricChip icon={<GroupsIcons />} value={`${organization.stats?.groups} Groups`} />
                <MetricChip icon={<AddTerminalIcon />} value={`${organization.stats?.terminals} Terminals`} />
                <MetricChip icon={<RssFeedIcon />} value={`${organization.stats?.activeDataPlans} Data plans`} />
                <MetricChip
                  icon={<AccountTreeIcon />}
                  value={`${organization.stats?.subOrganizations} Suborganization`}
                />
              </MetricChipsColumn>
            </Row>
            <HorizontalSpacer spacing={6} />
            {pendingInvites?.length > 0 && (
              <div>
                <PendingInvitesCard pendingInvites={pendingInvites} />
                <HorizontalSpacer spacing={2} />
              </div>
            )}
            <Row justify="between">
              <Col lg={5} md={12}>
                <form onSubmit={onSaveUpdateAddress}>
                  <Row justify="between" align="center" style={{ padding: theme.spacings.large }}>
                    <H3>Billing address</H3>
                    {!editingBilling && (
                      <PrimaryButton
                        title="Edit"
                        icon={<EditIcon />}
                        type="button"
                        onClick={() => setEditingBilling(true)}
                      />
                    )}
                    {editingBilling && (
                      <div style={{ display: "flex" }}>
                        <SecondaryButton title="Save" type="submit" disabled={!hasUpdatedBillingAddress} />
                        <Spacer spacing={1} />
                        <PrimaryButton title="Cancel" type="button" onClick={() => setEditingBilling(false)} />
                      </div>
                    )}
                  </Row>
                  <HorizontalSpacer spacing={1} />

                  <AddressForm
                    onChange={handleChangeBillingAddress}
                    disabled={!editingBilling}
                    address1Value={billingAddress?.address1}
                    address2Value={billingAddress?.address2}
                    cityValue={billingAddress?.city}
                    countryValue={billingAddress?.country}
                    zipCodeValue={billingAddress?.zipCode}
                    vatValue={billingAddress?.vat}
                  />
                </form>

                {data?.updateOrganization?.errors?.length > 0 && (
                  <div>
                    <HorizontalSpacer spacing={1} />
                    <H3 color={theme.colors.red}>{data?.updateOrganization?.errors[0].messages}</H3>
                    <HorizontalSpacer spacing={1} />
                  </div>
                )}
              </Col>
              <Visible md>
                <HorizontalSpacer spacing={2} />
              </Visible>
              <Col lg={5} md={12}>
                <Row align="center" style={{ padding: theme.spacings.large }}>
                  <div>
                    <H3>Shipping address</H3>
                  </div>
                  <Spacer spacing={2} />
                </Row>
                <HorizontalSpacer spacing={1} />
                <AddressForm
                  disabled={true}
                  address1Value={organization?.shippingAddress?.address1}
                  address2Value={organization?.shippingAddress?.address2}
                  cityValue={organization?.shippingAddress?.city}
                  countryValue={organization?.shippingAddress?.country}
                  zipCodeValue={organization?.shippingAddress?.zipCode}
                  hideVat
                />
              </Col>
            </Row>
          </ContentContainer>
          <HorizontalSpacer spacing={4} />
        </SlideOutHeight>
      </Card>
    </div>
  )
}

export default SuborganizationOverview
